/**
 * Multi-Trade Image Capture Configuration Panel (KAN-125)
 *
 * Admin component for configuring multi-trade table extraction.
 * Supports column-to-field mappings, value transforms, default values,
 * and allocation matrix configuration.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { tradeImageCaptureService } from '../../services/api/tradeImageCaptureService';
import { groupingsService } from '../../services/api/groupingsService';
import { bankService, Bank } from '../../services/api/bankService';
import { clientService, TradingEntity } from '../../services/api/clientService';
import { getEnabledCurrencies } from '../../types/currency';
import { GroupingsConfig } from '../../types/groupings';
import AlertModal from '../common/AlertModal';
import type {
  MultiTradeImageCaptureConfig,
  MultiTradeColumnMapping,
  AllocationConfig,
  ValueTransform,
  EntityDerivation,
} from '../../types/tradeImageCapture';
import { TRADE_DOCUMENT_FIELDS, TRADE_FIELD_ENUMS } from '../../constants/tradeFields';
import './TradeImageCaptureConfigPanel.css';

type PanelMode = 'loading' | 'empty' | 'analysing' | 'edit';

const ALLOCATION_DATA_FIELDS = [
  { value: 'amount', labelKey: 'tradeFields.amount' },
  { value: 'percentage', labelKey: 'admin.multiTradeCapture.percentage' },
];

const TRANSFORM_TYPES = [
  { value: 'DATE_FORMAT', label: 'Date Format' },
  { value: 'ENUM_MAP', label: 'Enum Map' },
  { value: 'NUMBER_FORMAT', label: 'Number Format' },
  { value: 'TEXT_REPLACE', label: 'Text Replace' },
];

const MultiTradeConfigPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  const [mode, setMode] = useState<PanelMode>('loading');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Config state
  const [enabled, setEnabled] = useState(true);
  const [sourceSystemName, setSourceSystemName] = useState('');
  const [detectionHints, setDetectionHints] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<MultiTradeColumnMapping[]>([]);
  const [valueTransforms, setValueTransforms] = useState<ValueTransform[]>([]);
  const [defaultValues, setDefaultValues] = useState<Record<string, string>>({});
  const [allocationConfig, setAllocationConfig] = useState<AllocationConfig | null>(null);
  const [allocationEnabled, setAllocationEnabled] = useState(false);
  const [entityDerivation, setEntityDerivation] = useState<EntityDerivation | null>(null);

  // New detection hint input
  const [newHint, setNewHint] = useState('');

  // New default value inputs
  const [newDefaultField, setNewDefaultField] = useState('');
  const [newDefaultValue, setNewDefaultValue] = useState('');

  // New column mapping inputs
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [newMappingSource, setNewMappingSource] = useState('');
  const [newMappingTargets, setNewMappingTargets] = useState<string[]>(['']);

  // Inline editing state
  const [editingMappingIndex, setEditingMappingIndex] = useState<number | null>(null);
  const [editingTargetIdx, setEditingTargetIdx] = useState<number | null>(null);
  const [expandedTransformIndex, setExpandedTransformIndex] = useState<number | null>(null);

  // Groupings config (for dynamic field labels)
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  // Dynamic enum data: counterparties (banks) and trading entities (ourParty)
  const [banks, setBanks] = useState<Bank[]>([]);
  const [tradingEntities, setTradingEntities] = useState<TradingEntity[]>([]);

  // Image state (for analysis mode and persisted source image)
  const [capturedImageDataUrl, setCapturedImageDataUrl] = useState<string | null>(null);
  const [capturedImageBase64, setCapturedImageBase64] = useState<string | null>(null);
  const [capturedImageMimeType, setCapturedImageMimeType] = useState<string | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Drag-and-drop for allocation header rows
  const headerDragItem = useRef<number | null>(null);
  const headerDragOverItem = useRef<number | null>(null);

  // Alert modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  const showSuccess = (message: string) => {
    setAlertModal({ isOpen: true, type: 'success', title: t('common.success'), message });
  };

  const showError = (message: string) => {
    setAlertModal({ isOpen: true, type: 'error', title: t('common.error'), message });
  };

  // Build FX+ fields list with dynamic grouping labels
  const fxPlusFields = useMemo(() => {
    const fields = TRADE_DOCUMENT_FIELDS.map((f) => ({ value: f.field, label: t(f.labelKey) }));
    const g1Label = groupingsConfig?.grouping1Label || 'Grouping 1';
    const g2Label = groupingsConfig?.grouping2Label || 'Grouping 2';
    fields.push({ value: 'grouping1', label: g1Label });
    fields.push({ value: 'grouping2', label: g2Label });
    if (groupingsConfig?.grouping3Label) {
      fields.push({ value: 'grouping3', label: groupingsConfig.grouping3Label });
    }
    return fields;
  }, [groupingsConfig, t]);

  // Build dynamic enum values
  const allEnums = useMemo(() => {
    const enums: Record<string, { value: string; label: string }[]> = {};
    for (const [field, values] of Object.entries(TRADE_FIELD_ENUMS)) {
      enums[field] = values.map((v) => ({ value: v.value, label: t(v.labelKey) }));
    }
    // Counterparties (banks)
    if (banks.length) {
      enums.counterparty = banks.map((b) => ({ value: b.name, label: b.name }));
    }
    // Trading entities (ourParty)
    if (tradingEntities.length) {
      enums.ourParty = tradingEntities.map((e) => ({ value: e.name, label: e.name }));
    }
    // Currencies
    const currencies = getEnabledCurrencies();
    if (currencies.length) {
      const currencyOptions = currencies.map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` }));
      enums.currency1 = currencyOptions;
      enums.currency2 = currencyOptions;
    }
    // Groupings
    if (groupingsConfig?.grouping1Values?.length) {
      enums.grouping1 = groupingsConfig.grouping1Values
        .filter((v) => v.isActive)
        .map((v) => ({ value: v.name, label: v.name }));
    }
    if (groupingsConfig?.grouping2Values?.length) {
      enums.grouping2 = groupingsConfig.grouping2Values
        .filter((v) => v.isActive)
        .map((v) => ({ value: v.name, label: v.name }));
    }
    if (groupingsConfig?.grouping3Values?.length) {
      enums.grouping3 = groupingsConfig.grouping3Values
        .filter((v) => v.isActive)
        .map((v) => ({ value: v.name, label: v.name }));
    }
    return enums;
  }, [groupingsConfig, banks, tradingEntities, t]);

  // Allocation header fields — use client-specific grouping labels
  const allocationHeaderFields = useMemo(() => {
    const fields = [
      { value: 'entityName', label: t('admin.multiTradeCapture.entityName') },
      { value: 'grouping1', label: groupingsConfig?.grouping1Label || t('admin.multiTradeCapture.grouping1') },
      { value: 'grouping2', label: groupingsConfig?.grouping2Label || t('admin.multiTradeCapture.grouping2') },
    ];
    if (groupingsConfig?.grouping3Label) {
      fields.push({ value: 'grouping3', label: groupingsConfig.grouping3Label });
    }
    fields.push({ value: 'hedgeObject', label: t('tradeFields.hedgeObject') });
    return fields;
  }, [groupingsConfig, t]);

  // Allocation-specific enum values (for ENUM_MAP transforms on allocation header fields)
  const allocationEnums = useMemo(() => {
    const enums: Record<string, { value: string; label: string }[]> = {};
    if (tradingEntities.length) {
      enums.entityName = tradingEntities.map((e) => ({ value: e.name, label: e.name }));
    }
    if (allEnums.grouping1) enums.grouping1 = allEnums.grouping1;
    if (allEnums.grouping2) enums.grouping2 = allEnums.grouping2;
    if (allEnums.grouping3) enums.grouping3 = allEnums.grouping3;
    return enums;
  }, [tradingEntities, allEnums]);

  // Expanded allocation transform index (tracks which header row has its transform config open)
  const [expandedAllocTransformIdx, setExpandedAllocTransformIdx] = useState<number | null>(null);

  // Fields already used in column mappings (exclude from default values dropdown)
  const mappedFields = useMemo(() => {
    const fields = new Set<string>();
    columnMappings.forEach((m) => m.targetFields.forEach((f) => fields.add(f)));
    return fields;
  }, [columnMappings]);

  // Fields available for defaults (not already mapped)
  const availableDefaultFields = useMemo(() => {
    return fxPlusFields.filter((f) => !mappedFields.has(f.value) && !defaultValues[f.value]);
  }, [fxPlusFields, mappedFields, defaultValues]);

  // Load config on mount
  useEffect(() => {
    if (!clientId) return;

    groupingsService.getGroupings(clientId).then((groupings) => {
      if (groupings) setGroupingsConfig(groupings);
    }).catch((err) => {
      console.error('Failed to load groupings config:', err);
    });

    // Load counterparties (banks) and trading entities for dynamic enum values
    Promise.all([
      bankService.listBanks(),
      clientService.getTradingEntities(clientId),
    ]).then(([banksResponse, entitiesResponse]) => {
      if (banksResponse.success) setBanks(banksResponse.data.filter(b => b.isCounterparty !== false));
      if (entitiesResponse.success) setTradingEntities(entitiesResponse.data);
    }).catch((err) => {
      console.error('Failed to load banks/trading entities:', err);
    });

    const loadConfig = async () => {
      try {
        const config = await tradeImageCaptureService.getMultiConfig(clientId);
        if (config) {
          setEnabled(config.enabled);
          setSourceSystemName(config.sourceSystemName || '');
          setDetectionHints(config.detectionHints || []);
          setColumnMappings(config.columnMappings || []);
          setValueTransforms(config.valueTransforms || []);
          setDefaultValues(config.defaultValues || {});
          setAllocationConfig(config.allocationConfig || null);
          // Backward compat: infer allocationEnabled if headerRows exist but field is absent
          const ac = config.allocationConfig;
          setAllocationEnabled(ac?.allocationEnabled ?? (ac?.headerRows?.length ? true : false));
          setEntityDerivation(ac?.entityDerivation || null);
          setMode('edit');
        } else {
          setMode('empty');
        }
      } catch (err) {
        console.error('Failed to load multi-trade config:', err);
        setMode('empty');
      }
    };
    loadConfig();
  }, [clientId]);

  // Load persisted source image if available
  useEffect(() => {
    if (!clientId || mode !== 'edit') return;
    tradeImageCaptureService.getMultiSourceImageUrl(clientId).then((url) => {
      if (url) setCapturedImageDataUrl(url);
    }).catch(() => {});
  }, [clientId, mode]);

  // ------------------------------------------------------------------
  // Screenshot capture (click-to-paste) + analyse
  // ------------------------------------------------------------------

  const handleCaptureClick = useCallback(async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        const imageType = clipboardItem.types.find((type) => type.startsWith('image/'));
        if (imageType) {
          const blob = await clipboardItem.getType(imageType);
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            setCapturedImageDataUrl(dataUrl);
            const base64 = dataUrl.split(',')[1];
            setCapturedImageBase64(base64);
            setCapturedImageMimeType(imageType);
            handleAnalyze(base64, imageType, blob.size);
          };
          reader.readAsDataURL(blob);
          return;
        }
      }
      showError('No image found in clipboard. Please copy a screenshot first.');
    } catch (err) {
      showError('Clipboard access is not available in this browser.');
    }
  }, [clientId]);

  const handleAnalyze = async (base64: string, mimeType: string, sizeBytes: number) => {
    setMode('analysing');
    try {
      // Backend returns { columnMappings, valueTransforms, sourceSystemName, ... }
      // directly — NOT the single-trade SourceAnalysisResult shape.
      const result = await tradeImageCaptureService.analyzeMultiSource(
        clientId, base64, mimeType, sizeBytes
      );

      console.log('[MultiTradeConfig] Analysis result:', JSON.stringify(result, null, 2));

      // Set source system name if detected
      const detectedName = ((result as any).sourceSystemName || '').trim();
      const genericNames = ['unknown', 'trading system', 'system', 'source system', 'n/a'];
      if (detectedName && !genericNames.includes(detectedName.toLowerCase())) {
        setSourceSystemName(detectedName);
      }

      // Backend already returns columnMappings in the correct shape:
      // { sourceColumn, targetFields[], confidence?, description?, coordinates? }
      const rawMappings = (result as any).columnMappings || [];
      const newMappings: MultiTradeColumnMapping[] = rawMappings
        .filter((cm: any) => cm && cm.sourceColumn && Array.isArray(cm.targetFields))
        .map((cm: any) => ({
          sourceColumn: cm.sourceColumn,
          targetFields: cm.targetFields,
        }));
      setColumnMappings(newMappings);

      // Convert value transforms
      const rawTransforms = (result as any).valueTransforms || [];
      const newTransforms: ValueTransform[] = rawTransforms
        .filter((vt: any) => vt && vt.targetField)
        .map((vt: any) => ({
          targetField: vt.targetField,
          transformType: vt.transformType || '',
          config: vt.transformType === 'ENUM_MAP' ? {} : (vt.config || {}),
        }));
      setValueTransforms(newTransforms);

      setMode('edit');
      showSuccess(t('admin.multiTradeCapture.analysisComplete'));
    } catch (err: any) {
      console.error('Multi-trade source analysis failed:', err);
      showError(err.message || 'Source analysis failed');
      setMode(capturedImageDataUrl ? 'edit' : 'empty');
    }
  };

  // Get field label helper
  const getFieldLabel = (fieldValue: string): string => {
    return fxPlusFields.find((f) => f.value === fieldValue)?.label || fieldValue;
  };

  // Find the transform associated with a target field
  const getTransformForTarget = (targetField: string): ValueTransform | undefined => {
    return valueTransforms.find((vt) => vt.targetField === targetField);
  };

  // Update a specific target field within a mapping
  const updateMappingTarget = (mappingIdx: number, targetIdx: number, newTarget: string) => {
    setColumnMappings((prev) => {
      const updated = [...prev];
      const targets = [...updated[mappingIdx].targetFields];
      const oldTarget = targets[targetIdx];
      targets[targetIdx] = newTarget;
      updated[mappingIdx] = { ...updated[mappingIdx], targetFields: targets };
      // Update associated transform targetField
      if (oldTarget !== newTarget) {
        setValueTransforms((prevVt) =>
          prevVt.map((vt) => vt.targetField === oldTarget ? { ...vt, targetField: newTarget } : vt)
        );
      }
      return updated;
    });
  };

  // Add a target field to an existing mapping
  const addTargetToMapping = (mappingIdx: number) => {
    setColumnMappings((prev) => {
      const updated = [...prev];
      const flags = [...(updated[mappingIdx].isForwardStartingOnly || updated[mappingIdx].targetFields.map(() => false))];
      flags.push(false);
      updated[mappingIdx] = {
        ...updated[mappingIdx],
        targetFields: [...updated[mappingIdx].targetFields, ''],
        isForwardStartingOnly: flags,
      };
      return updated;
    });
  };

  // Remove a target field from a mapping
  const removeTargetFromMapping = (mappingIdx: number, targetIdx: number) => {
    setColumnMappings((prev) => {
      const updated = [...prev];
      const removedTarget = updated[mappingIdx].targetFields[targetIdx];
      const targets = updated[mappingIdx].targetFields.filter((_, i) => i !== targetIdx);
      const flags = (updated[mappingIdx].isForwardStartingOnly || []).filter((_, i) => i !== targetIdx);
      if (targets.length === 0) {
        // Remove the entire mapping if no targets left
        return updated.filter((_, i) => i !== mappingIdx);
      }
      updated[mappingIdx] = { ...updated[mappingIdx], targetFields: targets, isForwardStartingOnly: flags };
      // Remove associated transform
      if (removedTarget) {
        setValueTransforms((prevVt) => prevVt.filter((vt) => vt.targetField !== removedTarget));
      }
      return updated;
    });
  };

  // Update or add a transform for a target field
  const updateTransformForTarget = (
    targetField: string,
    transformType: ValueTransform['transformType'] | undefined,
    config?: Record<string, any>,
  ) => {
    if (!transformType) {
      // Remove transform
      setValueTransforms((prev) => prev.filter((vt) => vt.targetField !== targetField));
      return;
    }
    setValueTransforms((prev) => {
      const idx = prev.findIndex((vt) => vt.targetField === targetField);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { targetField, transformType, config: config || {} };
        return updated;
      }
      return [...prev, { targetField, transformType, config: config || {} }];
    });
  };

  // --- Transform config editor (matches single-trade panel) ---

  const renderTransformConfig = (
    transformType: string,
    config: Record<string, any>,
    onChange: (config: Record<string, any>) => void,
    targetField: string,
    enumOverrides?: Record<string, { value: string; label: string }[]>,
  ) => {
    switch (transformType) {
      case 'DATE_FORMAT':
        return (
          <div className="transform-config-editor">
            <label>{t('admin.tradeImageCapture.sourceFormat')}:</label>
            <input
              type="text"
              className="form-input config-input"
              value={config.sourceFormat || ''}
              onChange={(e) => onChange({ ...config, sourceFormat: e.target.value })}
              placeholder="DD/MM/YYYY"
            />
          </div>
        );
      case 'ENUM_MAP': {
        const knownEnums = (enumOverrides || allEnums)[targetField];
        const currentMappings: Record<string, string> = config.mappings || {};

        if (knownEnums) {
          // Structured editor: show each FX+ value with an input for source value
          const reverseMappings: Record<string, string> = {};
          Object.entries(currentMappings).forEach(([src, tgt]) => {
            reverseMappings[tgt as string] = src;
          });

          return (
            <div className="transform-config-editor">
              <label>{t('admin.tradeImageCapture.enumMapLabel')}:</label>
              <div className="tic-enum-table">
                <div className="tic-enum-header">
                  <span>{t('admin.tradeImageCapture.fxPlusValue')}</span>
                  <span>{t('admin.tradeImageCapture.sourceSystemValue')}</span>
                </div>
                {knownEnums.map((enumVal) => (
                  <div key={enumVal.value} className="tic-enum-row">
                    <span className="tic-enum-fxplus">{enumVal.label} <span className="tic-enum-code">({enumVal.value})</span></span>
                    <input
                      type="text"
                      className="form-input config-input"
                      value={reverseMappings[enumVal.value] || ''}
                      onChange={(e) => {
                        const newReverse = { ...reverseMappings, [enumVal.value]: e.target.value };
                        const newMappings: Record<string, string> = {};
                        Object.entries(newReverse).forEach(([tgt, src]) => {
                          if (src.trim()) newMappings[src] = tgt;
                        });
                        onChange({ ...config, mappings: newMappings });
                      }}
                      placeholder={t('admin.tradeImageCapture.sourceValuePlaceholder')}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Fallback for fields without known enums: key-value pair inputs
        const entries = Object.entries(currentMappings);
        return (
          <div className="transform-config-editor">
            <label>{t('admin.tradeImageCapture.mappings')}:</label>
            {entries.map(([src, tgt], idx) => (
              <div key={idx} className="config-row">
                <input
                  type="text"
                  className="form-input config-input"
                  value={src}
                  onChange={(e) => {
                    const newMappings: Record<string, string> = {};
                    entries.forEach(([s, t], j) => {
                      newMappings[j === idx ? e.target.value : s] = t as string;
                    });
                    onChange({ ...config, mappings: newMappings });
                  }}
                  placeholder={t('admin.tradeImageCapture.sourceValue')}
                />
                <span style={{ color: '#64748b' }}>&rarr;</span>
                <input
                  type="text"
                  className="form-input config-input"
                  value={tgt as string}
                  onChange={(e) => {
                    const newMappings: Record<string, string> = {};
                    entries.forEach(([s, t], j) => {
                      newMappings[s] = j === idx ? e.target.value : (t as string);
                    });
                    onChange({ ...config, mappings: newMappings });
                  }}
                  placeholder={t('admin.tradeImageCapture.fxPlusValue')}
                />
                <button
                  className="btn-icon btn-icon-danger"
                  onClick={() => {
                    const newMappings = { ...currentMappings };
                    delete newMappings[src];
                    onChange({ ...config, mappings: newMappings });
                  }}
                >&times;</button>
              </div>
            ))}
            <button
              className="btn-secondary btn-sm"
              onClick={() => onChange({ ...config, mappings: { ...currentMappings, '': '' } })}
            >+ {t('admin.tradeImageCapture.addMapping')}</button>
          </div>
        );
      }
      case 'NUMBER_FORMAT':
        return (
          <div className="transform-config-editor">
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.thousandSeparator')}:</label>
              <input
                type="text"
                className="form-input config-input-small"
                value={config.thousandSeparator || ''}
                onChange={(e) => onChange({ ...config, thousandSeparator: e.target.value })}
                placeholder="."
                maxLength={1}
              />
            </div>
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.decimalSeparator')}:</label>
              <input
                type="text"
                className="form-input config-input-small"
                value={config.decimalSeparator || ''}
                onChange={(e) => onChange({ ...config, decimalSeparator: e.target.value })}
                placeholder=","
                maxLength={1}
              />
            </div>
          </div>
        );
      case 'TEXT_REPLACE':
        return (
          <div className="transform-config-editor">
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.find')}:</label>
              <input
                type="text"
                className="form-input config-input"
                value={config.find || ''}
                onChange={(e) => onChange({ ...config, find: e.target.value })}
              />
            </div>
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.replace')}:</label>
              <input
                type="text"
                className="form-input config-input"
                value={config.replace || ''}
                onChange={(e) => onChange({ ...config, replace: e.target.value })}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // --- Handlers ---

  const handleAddHint = () => {
    if (newHint.trim()) {
      setDetectionHints((prev) => [...prev, newHint.trim()]);
      setNewHint('');
    }
  };

  const handleRemoveHint = (index: number) => {
    setDetectionHints((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddColumnMapping = () => {
    if (!newMappingSource.trim() || newMappingTargets.every((t) => !t)) return;
    const validTargets = newMappingTargets.filter((t) => t);
    setColumnMappings((prev) => [
      ...prev,
      { sourceColumn: newMappingSource.trim(), targetFields: validTargets },
    ]);
    setNewMappingSource('');
    setNewMappingTargets(['']);
    setShowAddMapping(false);
  };

  const handleRemoveColumnMapping = (index: number) => {
    setColumnMappings((prev) => prev.filter((_, i) => i !== index));
    // Also remove associated transforms
    const removedMapping = columnMappings[index];
    if (removedMapping) {
      setValueTransforms((prev) =>
        prev.filter((vt) => !removedMapping.targetFields.includes(vt.targetField))
      );
    }
  };

  const handleAddDefaultValue = () => {
    if (!newDefaultField || !newDefaultValue.trim()) return;
    setDefaultValues((prev) => ({ ...prev, [newDefaultField]: newDefaultValue.trim() }));
    setNewDefaultField('');
    setNewDefaultValue('');
  };

  const handleRemoveDefaultValue = (field: string) => {
    setDefaultValues((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleAddAllocationHeaderRow = () => {
    setAllocationConfig((prev) => {
      const config = prev || { allocationEnabled: true, headerRows: [], dataRowField: 'amount', valueTransforms: [], defaultValues: {} };
      return { ...config, headerRows: [...config.headerRows, 'entityName'] };
    });
  };

  const handleRemoveAllocationHeaderRow = (index: number) => {
    setAllocationConfig((prev) => {
      if (!prev) return prev;
      const removedField = prev.headerRows[index];
      const updatedTransforms = prev.valueTransforms?.filter((vt) => vt.targetField !== removedField) || [];
      return { ...prev, headerRows: prev.headerRows.filter((_, i) => i !== index), valueTransforms: updatedTransforms };
    });
  };

  const handleHeaderRowDragStart = (idx: number) => {
    headerDragItem.current = idx;
  };

  const handleHeaderRowDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    headerDragOverItem.current = idx;
  };

  const handleHeaderRowDrop = () => {
    if (headerDragItem.current === null || headerDragOverItem.current === null) return;
    const from = headerDragItem.current;
    const to = headerDragOverItem.current;
    if (from === to) return;
    setAllocationConfig((prev) => {
      if (!prev) return prev;
      const updated = [...prev.headerRows];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      return { ...prev, headerRows: updated };
    });
    // Collapse any expanded transform to avoid index mismatch
    setExpandedAllocTransformIdx(null);
    headerDragItem.current = null;
    headerDragOverItem.current = null;
  };

  // Get allocation transform for a given header field
  const getAllocTransform = (field: string): ValueTransform | undefined => {
    return allocationConfig?.valueTransforms?.find((vt) => vt.targetField === field);
  };

  // Update or add an allocation transform
  const updateAllocTransform = (
    field: string,
    transformType: ValueTransform['transformType'] | undefined,
    config?: Record<string, any>,
  ) => {
    setAllocationConfig((prev) => {
      if (!prev) return prev;
      const transforms = prev.valueTransforms || [];
      if (!transformType) {
        return { ...prev, valueTransforms: transforms.filter((vt) => vt.targetField !== field) };
      }
      const idx = transforms.findIndex((vt) => vt.targetField === field);
      if (idx >= 0) {
        const updated = [...transforms];
        updated[idx] = { targetField: field, transformType, config: config || {} };
        return { ...prev, valueTransforms: updated };
      }
      return { ...prev, valueTransforms: [...transforms, { targetField: field, transformType, config: config || {} }] };
    });
  };

  const handleSave = async () => {
    // Validate allocation config when enabled
    if (allocationEnabled) {
      const headerRows = allocationConfig?.headerRows || [];
      if (!entityDerivation) {
        // Path A: entityName must be in header rows
        if (!headerRows.includes('entityName')) {
          showError(t('admin.multiTradeCapture.entityNameRequired'));
          return;
        }
      } else {
        // Path B: source field must be in header rows
        if (!headerRows.includes(entityDerivation.sourceField)) {
          showError(t('admin.multiTradeCapture.sourceFieldRequired'));
          return;
        }
        // At least one mapping must be defined
        if (Object.keys(entityDerivation.mappings).length === 0) {
          showError(t('admin.multiTradeCapture.entityMappingsRequired'));
          return;
        }
        // If substring enabled, both start and length must be provided
        if (entityDerivation.substring && (entityDerivation.substring.start == null || entityDerivation.substring.length == null)) {
          showError(t('admin.multiTradeCapture.substringRequired'));
          return;
        }
      }
    }

    setSaving(true);
    try {
      // Build allocation config with enabled flag and entity derivation
      // Strip transient _testInput from entityDerivation before saving
      const cleanedDerivation: EntityDerivation | undefined = entityDerivation
        ? (() => {
            const { _testInput, ...rest } = entityDerivation as any;
            return rest as EntityDerivation;
          })()
        : undefined;

      const savedAllocConfig: AllocationConfig | undefined = allocationConfig
        ? {
            ...allocationConfig,
            allocationEnabled,
            entityDerivation: cleanedDerivation,
          }
        : allocationEnabled
          ? { allocationEnabled, headerRows: [], dataRowField: 'amount', valueTransforms: [], defaultValues: {}, entityDerivation: cleanedDerivation }
          : undefined;

      const config: MultiTradeImageCaptureConfig = {
        enabled,
        sourceSystemName,
        detectionHints,
        columnMappings,
        valueTransforms,
        defaultValues,
        allocationConfig: savedAllocConfig,
      };
      await tradeImageCaptureService.saveMultiConfig(
        clientId,
        config,
        capturedImageBase64 || undefined,
        capturedImageMimeType || undefined,
      );
      // Clear captured image data after save (it's now persisted on backend)
      setCapturedImageBase64(null);
      setCapturedImageMimeType(null);
      showSuccess(t('admin.multiTradeCapture.saveSuccess'));
      setMode('edit');
    } catch (err: any) {
      console.error('Failed to save multi-trade config:', err);
      showError(t('admin.multiTradeCapture.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await tradeImageCaptureService.deleteMultiConfig(clientId);
      showSuccess(t('admin.multiTradeCapture.deleteSuccess'));
      // Reset all state
      setEnabled(true);
      setSourceSystemName('');
      setDetectionHints([]);
      setColumnMappings([]);
      setValueTransforms([]);
      setDefaultValues({});
      setAllocationConfig(null);
      setMode('empty');
    } catch (err: any) {
      console.error('Failed to delete multi-trade config:', err);
      showError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // --- Render ---

  if (mode === 'loading') {
    return (
      <div className="tab-loading"><div className="spinner" /><p>{t('common.loading')}</p></div>
    );
  }

  if (mode === 'empty') {
    return (
      <div>
        <h3 className="tic-title">{t('admin.multiTradeCapture.emptyStateTitle')}</h3>
        <div className="tic-empty-state">
          <p>{t('admin.multiTradeCapture.pasteTableScreenshot')}</p>
          <div className="tic-empty-actions">
            <div
              className="trade-image-capture-zone tic-capture-zone"
              onClick={handleCaptureClick}
            >
              {t('admin.multiTradeCapture.pasteScreenshot')}
            </div>
            <button className="btn-secondary" onClick={() => setMode('edit')}>
              {t('admin.multiTradeCapture.configureManually')}
            </button>
          </div>
        </div>
        <AlertModal
          isOpen={alertModal.isOpen}
          onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
          type={alertModal.type}
          title={alertModal.title}
          message={alertModal.message}
        />
      </div>
    );
  }

  if (mode === 'analysing') {
    return (
      <div>
        <h3 className="tic-title">{t('admin.multiTradeCapture.title')}</h3>
        <div className="tic-analysing">
          <div className="capture-spinner" />
          <span>{t('admin.tradeImageCapture.analysing')}</span>
        </div>
        {capturedImageDataUrl && (
          <div className="tic-analysing-image">
            <img src={capturedImageDataUrl} alt="Analysing" />
          </div>
        )}
      </div>
    );
  }

  const hasCapturedImage = !!capturedImageDataUrl;

  return (
    <div>
      <h3 className="tic-title">{t('admin.multiTradeCapture.title')}</h3>

      {/* Enable toggle */}
      <div className="tic-field-group" style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          {t('admin.multiTradeCapture.enableToggle')}
        </label>
      </div>

      {/* Split view (image left, config right) or full-width */}
      <div className={`tic-content ${hasCapturedImage ? 'tic-split' : 'tic-full'}`}>
        {/* Left: Image panel */}
        {hasCapturedImage && (
          <div className="tic-image-panel">
            <div className="tic-field-group">
              <label className="tic-field-label">{t('admin.tradeImageCapture.sourceSystemName')}</label>
              <input
                type="text"
                className="form-input"
                value={sourceSystemName}
                onChange={(e) => setSourceSystemName(e.target.value)}
                placeholder="e.g. Treasury System"
              />
            </div>

            <div className="tic-image-container">
              <img
                ref={imageRef}
                src={capturedImageDataUrl}
                alt="Source table"
                className="tic-source-image"
              />
            </div>
            <div
              className="trade-image-capture-zone tic-capture-zone tic-recapture"
              onClick={handleCaptureClick}
            >
              {t('admin.multiTradeCapture.pasteNewScreenshot')}
            </div>
          </div>
        )}

        {/* Right (or full-width): Config form */}
        <div className="tic-form-panel">
          {/* Source System Name — in form panel when no image */}
          {!hasCapturedImage && (
            <div className="tic-field-group">
              <label className="tic-field-label">{t('admin.tradeImageCapture.sourceSystemName')}</label>
              <input
                type="text"
                className="form-input"
                value={sourceSystemName}
                onChange={(e) => setSourceSystemName(e.target.value)}
                placeholder="e.g. Treasury System"
              />
            </div>
          )}

          {/* Paste zone when no image yet (in edit mode) */}
          {!hasCapturedImage && (
            <div className="tic-field-group">
              <div
                className="trade-image-capture-zone tic-capture-zone"
                onClick={handleCaptureClick}
              >
                {t('admin.multiTradeCapture.pasteScreenshot')}
              </div>
            </div>
          )}

          {/* Column Mappings — table format matching single-trade panel */}
          <div className="tic-section">
            <div className="tic-section-header">
              <h4>{t('admin.multiTradeCapture.columnMappings')}</h4>
              <button className="btn-secondary btn-sm" onClick={() => setShowAddMapping(true)}>
                {t('admin.multiTradeCapture.addColumnMapping')}
              </button>
            </div>

            <div className="tic-mappings-table">
              <div className="tic-table-header mtc-table-header">
                <span>{t('admin.multiTradeCapture.sourceColumn')}</span>
                <span>{t('admin.multiTradeCapture.targetFields')}</span>
                <span className="tic-cell-fwdstarting">{t('admin.multiTradeCapture.fwdStarting')}</span>
                <span>{t('admin.tradeImageCapture.transformType')}</span>
                <span>{t('admin.tradeImageCapture.action')}</span>
              </div>

              {columnMappings.map((mapping, idx) => (
                <React.Fragment key={idx}>
                  {mapping.targetFields.map((tf, tIdx) => {
                    const transform = getTransformForTarget(tf);
                    return (
                      <div key={`${idx}-${tIdx}`} className="tic-table-row mtc-table-row">
                        {/* Source column — only show on first target row */}
                        <span className="tic-cell-source">
                          {tIdx === 0 ? mapping.sourceColumn : ''}
                        </span>

                        {/* Target field — click to edit */}
                        <span
                          className="tic-cell-target tic-cell-editable"
                          onClick={() => { setEditingMappingIndex(idx); setEditingTargetIdx(tIdx); }}
                        >
                          {editingMappingIndex === idx && editingTargetIdx === tIdx ? (
                            <select
                              className="form-select form-select-sm"
                              value={tf}
                              onChange={(e) => {
                                updateMappingTarget(idx, tIdx, e.target.value);
                                setEditingMappingIndex(null);
                                setEditingTargetIdx(null);
                              }}
                              onBlur={() => { setEditingMappingIndex(null); setEditingTargetIdx(null); }}
                              autoFocus
                            >
                              {fxPlusFields.map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </select>
                          ) : (
                            getFieldLabel(tf)
                          )}
                        </span>

                        {/* KAN-190: Forward Starting flag */}
                        <span className="tic-cell-fwdstarting">
                          <input
                            type="checkbox"
                            checked={!!(mapping.isForwardStartingOnly && mapping.isForwardStartingOnly[tIdx])}
                            onChange={(e) => {
                              setColumnMappings((prev) => {
                                const updated = [...prev];
                                const flags = [...(updated[idx].isForwardStartingOnly || updated[idx].targetFields.map(() => false))];
                                // Ensure flags array matches targetFields length
                                while (flags.length < updated[idx].targetFields.length) flags.push(false);
                                flags[tIdx] = e.target.checked;
                                updated[idx] = { ...updated[idx], isForwardStartingOnly: flags };
                                return updated;
                              });
                            }}
                            title={t('admin.multiTradeCapture.fwdStartingTooltip')}
                          />
                        </span>

                        {/* Transform — pill or add button */}
                        <span className="tic-cell-transform">
                          {transform ? (
                            <span
                              className="tic-transform-pill"
                              onClick={() => setExpandedTransformIndex(expandedTransformIndex === idx * 100 + tIdx ? null : idx * 100 + tIdx)}
                            >
                              {TRANSFORM_TYPES.find((tt) => tt.value === transform.transformType)?.label || transform.transformType}
                            </span>
                          ) : (
                            <button
                              className="btn-icon tic-add-transform-btn"
                              title={t('admin.tradeImageCapture.addTransform')}
                              onClick={() => {
                                updateTransformForTarget(tf, 'DATE_FORMAT', {});
                                setExpandedTransformIndex(idx * 100 + tIdx);
                              }}
                            >
                              +
                            </button>
                          )}
                        </span>

                        {/* Actions */}
                        <span className="tic-cell-actions">
                          {tIdx === 0 && mapping.targetFields.length === 1 && (
                            <button
                              className="btn-icon"
                              title={t('admin.multiTradeCapture.addTargetField')}
                              onClick={() => addTargetToMapping(idx)}
                            >
                              +
                            </button>
                          )}
                          {tIdx === 0 && mapping.targetFields.length > 1 && (
                            <button
                              className="btn-icon"
                              title={t('admin.multiTradeCapture.addTargetField')}
                              onClick={() => addTargetToMapping(idx)}
                            >
                              +
                            </button>
                          )}
                          <button
                            className="btn-icon btn-icon-danger"
                            title={t('admin.tradeImageCapture.remove')}
                            onClick={() => removeTargetFromMapping(idx, tIdx)}
                          >
                            &times;
                          </button>
                        </span>
                      </div>
                    );
                  })}

                  {/* Expanded transform config row */}
                  {mapping.targetFields.map((tf, tIdx) => {
                    const transform = getTransformForTarget(tf);
                    if (expandedTransformIndex !== idx * 100 + tIdx || !transform) return null;
                    return (
                      <div key={`expand-${idx}-${tIdx}`} className="tic-row-expand">
                        <div className="tic-row-expand-header">
                          <select
                            className="form-select form-select-sm"
                            value={transform.transformType}
                            onChange={(e) => updateTransformForTarget(tf, e.target.value as ValueTransform['transformType'], {})}
                          >
                            {TRANSFORM_TYPES.map((tt) => (
                              <option key={tt.value} value={tt.value}>{tt.label}</option>
                            ))}
                          </select>
                          <button
                            className="btn-icon"
                            title={t('common.close')}
                            onClick={() => setExpandedTransformIndex(null)}
                          >
                            ▲
                          </button>
                        </div>
                        {renderTransformConfig(
                          transform.transformType,
                          transform.config || {},
                          (newConfig) => updateTransformForTarget(tf, transform.transformType, newConfig),
                          tf,
                        )}
                        <div className="tic-row-expand-footer">
                          <button
                            className="btn-primary btn-sm"
                            onClick={() => setExpandedTransformIndex(null)}
                          >
                            {t('common.ok')}
                          </button>
                          <button
                            className="btn-secondary btn-sm tic-remove-transform-btn"
                            onClick={() => {
                              updateTransformForTarget(tf, undefined);
                              setExpandedTransformIndex(null);
                            }}
                          >
                            {t('admin.tradeImageCapture.removeTransform')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}

              {columnMappings.length === 0 && !showAddMapping && (
                <div className="tic-table-empty">{t('admin.multiTradeCapture.noColumnMappings')}</div>
              )}
            </div>

            {/* Add mapping inline form */}
            {showAddMapping && (
              <div className="tic-add-form">
                <input
                  type="text"
                  className="form-input"
                  value={newMappingSource}
                  onChange={(e) => setNewMappingSource(e.target.value)}
                  placeholder={t('admin.multiTradeCapture.sourceColumn')}
                />
                <select
                  className="form-select"
                  value={newMappingTargets[0] || ''}
                  onChange={(e) => setNewMappingTargets([e.target.value])}
                >
                  <option value="">{t('admin.multiTradeCapture.targetFields')}</option>
                  {fxPlusFields.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <div className="tic-add-form-actions">
                  <button className="btn-primary btn-sm" onClick={handleAddColumnMapping}>
                    {t('admin.tradeImageCapture.add')}
                  </button>
                  <button className="btn-secondary btn-sm" onClick={() => { setShowAddMapping(false); setNewMappingSource(''); setNewMappingTargets(['']); }}>
                    {t('admin.tradeImageCapture.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Default Values — table format */}
          <div className="tic-section">
            <div className="tic-section-header">
              <h4>{t('admin.multiTradeCapture.defaultValues')}</h4>
            </div>

            <div className="tic-mappings-table">
              <div className="tic-table-header mtc-defaults-header">
                <span>{t('admin.multiTradeCapture.defaultField')}</span>
                <span>{t('admin.multiTradeCapture.defaultValue')}</span>
                <span>{t('admin.tradeImageCapture.action')}</span>
              </div>

              {Object.entries(defaultValues).map(([field, value]) => (
                <div key={field} className="tic-table-row mtc-defaults-row">
                  <span className="tic-cell-source">{getFieldLabel(field)}</span>
                  <span className="tic-cell-target">
                    {allEnums[field] ? (
                      <select
                        className="form-select form-select-sm"
                        value={value}
                        onChange={(e) => setDefaultValues((prev) => ({ ...prev, [field]: e.target.value }))}
                      >
                        {allEnums[field].map((ev) => (
                          <option key={ev.value} value={ev.value}>{ev.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-input form-input-sm"
                        value={value}
                        onChange={(e) => setDefaultValues((prev) => ({ ...prev, [field]: e.target.value }))}
                      />
                    )}
                  </span>
                  <span className="tic-cell-actions">
                    <button
                      className="btn-icon btn-icon-danger"
                      title={t('admin.tradeImageCapture.remove')}
                      onClick={() => handleRemoveDefaultValue(field)}
                    >
                      &times;
                    </button>
                  </span>
                </div>
              ))}

              {Object.keys(defaultValues).length === 0 && (
                <div className="tic-table-empty">{t('admin.multiTradeCapture.noDefaults')}</div>
              )}
            </div>

            {/* Add default inline */}
            <div className="tic-add-form">
              <select
                className="form-select"
                value={newDefaultField}
                onChange={(e) => setNewDefaultField(e.target.value)}
              >
                <option value="">{t('admin.multiTradeCapture.defaultField')}</option>
                {availableDefaultFields.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              {newDefaultField && allEnums[newDefaultField] ? (
                <select
                  className="form-select"
                  value={newDefaultValue}
                  onChange={(e) => setNewDefaultValue(e.target.value)}
                >
                  <option value="">{t('admin.multiTradeCapture.defaultValue')}</option>
                  {allEnums[newDefaultField].map((ev) => (
                    <option key={ev.value} value={ev.value}>{ev.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-input"
                  value={newDefaultValue}
                  onChange={(e) => setNewDefaultValue(e.target.value)}
                  placeholder={t('admin.multiTradeCapture.defaultValue')}
                />
              )}
              <div className="tic-add-form-actions">
                <button className="btn-primary btn-sm" onClick={handleAddDefaultValue}>+</button>
              </div>
            </div>
          </div>

          {/* Allocation Mapping */}
          <div className="tic-field-group" style={{ borderTop: '1px solid var(--border-color, #e2e8f0)', paddingTop: '1rem', marginTop: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <input
                type="checkbox"
                checked={allocationEnabled}
                onChange={(e) => setAllocationEnabled(e.target.checked)}
              />
              <span className="tic-field-label" style={{ marginBottom: 0 }}>{t('admin.multiTradeCapture.allocationMapping')}</span>
            </label>

            {allocationEnabled && (
              <>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
                  {t('admin.multiTradeCapture.allocationHelp')}
                </p>

                {/* Entity Resolution */}
                <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'var(--bg-secondary, #1e293b)', borderRadius: '6px', border: '1px solid var(--border-color, #334155)' }}>
                  <label className="tic-field-label" style={{ fontSize: '0.85rem' }}>{t('admin.multiTradeCapture.entityResolution')}</label>

                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="entityResolution"
                        checked={!entityDerivation}
                        onChange={() => setEntityDerivation(null)}
                      />
                      {t('admin.multiTradeCapture.entityDirect')}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="entityResolution"
                        checked={!!entityDerivation}
                        onChange={() => setEntityDerivation({ sourceField: allocationHeaderFields.filter(f => f.value !== 'entityName')[0]?.value || 'grouping1', mappings: {} })}
                      />
                      {t('admin.multiTradeCapture.entityDerived')}
                    </label>
                  </div>

                  {/* Path A hint */}
                  {!entityDerivation && (
                    <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>
                      {t('admin.multiTradeCapture.entityDirectHelp')}
                    </p>
                  )}

                  {/* Path B: Derivation config */}
                  {entityDerivation && (
                    <div style={{ marginTop: '0.5rem' }}>
                      {/* Source field, substring, and test — all on one line */}
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.85rem' }}>{t('admin.multiTradeCapture.deriveFrom')}:</span>
                        <select
                          className="form-select"
                          value={entityDerivation.sourceField}
                          onChange={(e) => setEntityDerivation(prev => prev ? { ...prev, sourceField: e.target.value } : prev)}
                          style={{ width: '180px' }}
                        >
                          {allocationHeaderFields
                            .filter(f => f.value !== 'entityName')
                            .map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                        </select>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer', marginLeft: '0.25rem' }}>
                          <input
                            type="checkbox"
                            checked={!!entityDerivation.substring}
                            onChange={(e) => {
                              setEntityDerivation(prev => prev
                                ? { ...prev, substring: e.target.checked ? { start: undefined as any, length: undefined as any } : undefined }
                                : prev
                              );
                            }}
                          />
                          {t('admin.multiTradeCapture.useSubstring')}
                        </label>

                        {entityDerivation.substring && (
                          <>
                            <span style={{ fontSize: '0.85rem' }}>{t('admin.multiTradeCapture.substringStart')}:</span>
                            <input
                              type="number"
                              className="form-input"
                              value={entityDerivation.substring.start ?? ''}
                              onChange={(e) => setEntityDerivation(prev => prev && prev.substring
                                ? { ...prev, substring: { ...prev.substring, start: e.target.value === '' ? undefined as any : parseInt(e.target.value) } }
                                : prev
                              )}
                              style={{ width: '55px' }}
                              min={0}
                              placeholder=""
                            />
                            <span style={{ fontSize: '0.85rem' }}>{t('admin.multiTradeCapture.substringLength')}:</span>
                            <input
                              type="number"
                              className="form-input"
                              value={entityDerivation.substring.length ?? ''}
                              onChange={(e) => setEntityDerivation(prev => prev && prev.substring
                                ? { ...prev, substring: { ...prev.substring, length: e.target.value === '' ? undefined as any : parseInt(e.target.value) } }
                                : prev
                              )}
                              style={{ width: '55px' }}
                              min={1}
                              placeholder=""
                            />
                            <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>|</span>
                            <span style={{ fontSize: '0.85rem' }}>Test:</span>
                            <input
                              type="text"
                              className="form-input config-input"
                              value={(entityDerivation as any)._testInput || ''}
                              onChange={(e) => setEntityDerivation(prev => prev ? { ...prev, _testInput: e.target.value } as any : prev)}
                              placeholder={t('admin.multiTradeCapture.substringTestPlaceholder')}
                              style={{ width: '120px' }}
                            />
                            <span className="tic-substring-arrow">→</span>
                            <span className="tic-substring-result">
                              {(() => {
                                const input = (entityDerivation as any)._testInput || '';
                                if (!input || entityDerivation.substring!.start == null || entityDerivation.substring!.length == null) return '—';
                                const start = entityDerivation.substring!.start;
                                const len = entityDerivation.substring!.length;
                                return input.substring(start, start + len) || '—';
                              })()}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Entity mappings table */}
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem', display: 'block' }}>{t('admin.multiTradeCapture.entityMappings')}:</label>
                        <div className="tic-enum-table">
                          <div className="tic-enum-header">
                            <span>{t('admin.tradeImageCapture.fxPlusValue')}</span>
                            <span>{t('admin.tradeImageCapture.sourceSystemValue')}</span>
                          </div>
                          {tradingEntities.map(entity => {
                            const reverseMappings: Record<string, string> = {};
                            Object.entries(entityDerivation.mappings).forEach(([src, tgt]) => {
                              reverseMappings[tgt] = src;
                            });
                            return (
                              <div key={entity.id} className="tic-enum-row">
                                <span className="tic-enum-fxplus">{entity.name}</span>
                                <input
                                  type="text"
                                  className="form-input config-input"
                                  value={reverseMappings[entity.name] || ''}
                                  onChange={(e) => {
                                    const newReverse = { ...reverseMappings, [entity.name]: e.target.value };
                                    const newMappings: Record<string, string> = {};
                                    Object.entries(newReverse).forEach(([tgt, src]) => {
                                      if (src.trim()) newMappings[src] = tgt;
                                    });
                                    setEntityDerivation(prev => prev ? { ...prev, mappings: newMappings } : prev);
                                  }}
                                  placeholder=""
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Header rows with inline transform */}
                {(allocationConfig?.headerRows || []).map((hr, idx) => {
                  const allocTransform = getAllocTransform(hr);
                  return (
                    <React.Fragment key={idx}>
                      <div
                        style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}
                        draggable
                        onDragStart={() => handleHeaderRowDragStart(idx)}
                        onDragOver={(e) => handleHeaderRowDragOver(e, idx)}
                        onDrop={handleHeaderRowDrop}
                      >
                        <span className="drag-handle" style={{ cursor: 'grab', fontSize: '1rem', color: 'var(--text-secondary)', userSelect: 'none' }}>≡</span>
                        <span style={{ minWidth: '80px', fontSize: '0.85rem' }}>{t('admin.multiTradeCapture.headerRow')} {idx + 1}:</span>
                        <select
                          className="form-select"
                          value={hr}
                          onChange={(e) => {
                            const oldField = hr;
                            const newField = e.target.value;
                            setAllocationConfig((prev) => {
                              if (!prev) return prev;
                              const updated = [...prev.headerRows];
                              updated[idx] = newField;
                              // Update associated transform targetField
                              const transforms = (prev.valueTransforms || []).map((vt) =>
                                vt.targetField === oldField ? { ...vt, targetField: newField } : vt
                              );
                              return { ...prev, headerRows: updated, valueTransforms: transforms };
                            });
                          }}
                          style={{ flex: 1 }}
                        >
                          {allocationHeaderFields.map((f) => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                        {/* Transform pill */}
                        <span className="tic-cell-transform">
                          {allocTransform ? (
                            <span
                              className="tic-transform-pill"
                              onClick={() => setExpandedAllocTransformIdx(expandedAllocTransformIdx === idx ? null : idx)}
                            >
                              {TRANSFORM_TYPES.find((tt) => tt.value === allocTransform.transformType)?.label || allocTransform.transformType}
                            </span>
                          ) : (
                            <button
                              className="btn-icon tic-add-transform-btn"
                              title={t('admin.tradeImageCapture.addTransform')}
                              onClick={() => {
                                updateAllocTransform(hr, 'ENUM_MAP', {});
                                setExpandedAllocTransformIdx(idx);
                              }}
                            >
                              +
                            </button>
                          )}
                        </span>
                        <button className="btn-icon btn-danger-icon" onClick={() => handleRemoveAllocationHeaderRow(idx)}>&times;</button>
                      </div>
                      {/* Expanded transform config */}
                      {expandedAllocTransformIdx === idx && allocTransform && (
                        <div className="tic-row-expand" style={{ marginBottom: '0.5rem' }}>
                          <div className="tic-row-expand-header">
                            <select
                              className="form-select form-select-sm"
                              value={allocTransform.transformType}
                              onChange={(e) => updateAllocTransform(hr, e.target.value as ValueTransform['transformType'], {})}
                            >
                              {TRANSFORM_TYPES.map((tt) => (
                                <option key={tt.value} value={tt.value}>{tt.label}</option>
                              ))}
                            </select>
                            <button
                              className="btn-icon"
                              title={t('common.close')}
                              onClick={() => setExpandedAllocTransformIdx(null)}
                            >
                              ▲
                            </button>
                          </div>
                          {renderTransformConfig(
                            allocTransform.transformType,
                            allocTransform.config || {},
                            (newConfig) => updateAllocTransform(hr, allocTransform.transformType, newConfig),
                            hr,
                            allocationEnums,
                          )}
                          <div className="tic-row-expand-footer">
                            <button
                              className="btn-primary btn-sm"
                              onClick={() => setExpandedAllocTransformIdx(null)}
                            >
                              {t('common.ok')}
                            </button>
                            <button
                              className="btn-secondary btn-sm tic-remove-transform-btn"
                              onClick={() => {
                                updateAllocTransform(hr, undefined);
                                setExpandedAllocTransformIdx(null);
                              }}
                            >
                              {t('admin.tradeImageCapture.removeTransform')}
                            </button>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}

                <button className="btn-link btn-sm" onClick={handleAddAllocationHeaderRow}>
                  + {t('admin.multiTradeCapture.addHeaderRow')}
                </button>

                {/* Data row field */}
                {allocationConfig && allocationConfig.headerRows.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.75rem' }}>
                    <span style={{ fontSize: '0.85rem' }}>{t('admin.multiTradeCapture.dataRowField')}:</span>
                    <select
                      className="form-select"
                      value={allocationConfig.dataRowField}
                      onChange={(e) => {
                        setAllocationConfig((prev) => prev ? { ...prev, dataRowField: e.target.value } : prev);
                      }}
                      style={{ width: '150px' }}
                    >
                      {ALLOCATION_DATA_FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>{t(f.labelKey)}</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>
        </div>{/* end tic-form-panel */}
      </div>{/* end tic-content */}

      {/* Actions */}
      <div className="tic-actions" style={{ marginTop: '1.5rem' }}>
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={saving || deleting}
        >
          {saving ? '...' : t('admin.tradeImageCapture.save')}
        </button>
        {mode === 'edit' && (
          <button
            className="btn-danger"
            onClick={handleDelete}
            disabled={saving || deleting}
          >
            {deleting ? '...' : t('admin.multiTradeCapture.deleteConfig')}
          </button>
        )}
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default MultiTradeConfigPanel;
